import React, { FC, useEffect, useRef, useState } from 'react';
import { BlockPath, useCurrentBlock } from '@/editor/PageEditor/CurrentBlockProvider';
import { useTranslation } from 'react-i18next';
import Container from '@/components/Container';
import { ReferenceType, ReferencesBlockModel } from '@/declarations/models/blocks/ReferencesBlock';
import ReferenceItem from '@/editor/PageEditor/EditorBlock/ReferencesBlock/ReferenceItem';
import { useControlledFieldArray } from '@/hooks/useControlledFieldArray';
import { BlockSpecificPage } from '@/editor/lib/declarations/BlockSpecificPage';
import { Button } from '@mui/material';
import { Draggable } from '@/declarations/Draggable';

const ProgramBlock: FC = () => {
  const { blockPath } = useCurrentBlock();
  const { t } = useTranslation('components');

  const {
    fields: referenceItems,
    append,
    move,
    remove,
  } = useControlledFieldArray<BlockSpecificPage<ReferencesBlockModel>, `${BlockPath}.items`>({
    name: `${blockPath}.items`,
  });

  const [closeAll, setCloseAll] = useState<boolean>(false);

  // Use a ref to track the initial append
  const hasAppendedInitialItem = useRef(false);

  const [newlyAddedItemIndex, setNewlyAddedItemIndex] = useState<number | null>(null);

  // Add an empty program time item when the component mounts
  useEffect(() => {
    if (!hasAppendedInitialItem.current && referenceItems.length === 0) {
      append({
        type: 'reference',
        referenceType: ReferenceType.ARTICLE,
      });
      hasAppendedInitialItem.current = true;
      setNewlyAddedItemIndex(0);
    }
  }, [referenceItems.length, append]);

  const handleAddReferenceItem = () => {
    append({
      type: 'reference',
      referenceType: ReferenceType.ARTICLE,
    });
    setNewlyAddedItemIndex(referenceItems.length);
  };

  const onMove = (fromIndex: number, toIndex: number) => {
    setCloseAll(true);
    setNewlyAddedItemIndex(null);
    move(fromIndex, toIndex);
    setTimeout(() => {
      setCloseAll(false);
    }, 0);
  };
  const onRemove = (index: number) => {
    setCloseAll(true);
    setNewlyAddedItemIndex(null);
    remove(index);
    setTimeout(() => {
      setCloseAll(false);
    }, 0);
  };

  return (
    <Container fullWidth column gap='3px'>
      {(referenceItems || []).map((item, index) => {
        const uniqueKey = `${item.id || index}`;
        const initiallyOpen = index === newlyAddedItemIndex;
        return (
          <>
            {!closeAll && (
              <ReferenceItem
                key={uniqueKey}
                item={item}
                itemPathPrefix={`${blockPath}.items.${index}`}
                onReorder={onMove}
                onRemove={onRemove}
                index={index}
                type={Draggable.ITEM}
                initiallyOpen={initiallyOpen}
              />
            )}
          </>
        );
      })}
      <Container mt={1}>
        <Button variant='outlined' color='secondary' onClick={handleAddReferenceItem}>
          {t('ReferencesBlock.addReference')}
        </Button>
      </Container>
    </Container>
  );
};

export default ProgramBlock;
